import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { getGeo } from "@/lib/geo";
import { toast } from "sonner";
import { Siren, Loader2, X, Phone } from "lucide-react";
import { Timeline } from "@/components/Timeline";

const ACTIVE = ["OPEN", "ACKNOWLEDGED", "DISPATCHED", "ON_SITE"];

const pos = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition((p) => resolve(p.coords), () => resolve(null), { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 });
});

// Floating SOS for Care customers/guardians: raises an emergency case for the HOMIIE Care team.
export function CareSosButton() {
    const [open, setOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [memberId, setMemberId] = useState("");
    const [msg, setMsg] = useState("");
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(null);
    const [count, setCount] = useState(null);

    useEffect(() => {
        if (count === null) return;
        if (count === 0) { setCount(null); send(); return; }
        const t = setTimeout(() => setCount((c) => (c === null ? null : c - 1)), 1000);
        return () => { clearTimeout(t); };
    }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCases = () => api.get("/care/sos").then((r) => setActive((r.data || []).find((c) => ACTIVE.includes(c.status)) || null)).catch(() => {});
    useEffect(() => { loadCases(); const t = setInterval(loadCases, 20000); return () => { clearInterval(t); }; }, []);
    useEffect(() => {
        if (!open) return;
        api.get("/care/members").then((r) => { setMembers(r.data || []); setMemberId((r.data || [])[0]?.id || ""); }).catch(() => {});
    }, [open]);

    const send = async () => {
        setBusy(true);
        try {
            const c = await pos();
            const g = getGeo() || {};
            const { data } = await api.post("/care/sos", { member_id: memberId || undefined, message: msg.trim() || undefined, lat: c?.latitude ?? g.lat, lng: c?.longitude ?? g.lng, address: g.address_line || g.display_name });
            toast.success(`SOS sent (${data.case_id}). HOMIIE Care team is responding now.`, { duration: 8000 });
            setActive(data); setMsg("");
        } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button data-testid="care-sos-btn" onClick={() => setOpen(true)} className={`fixed bottom-24 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white shadow-xl shadow-red-600/40 active:scale-95 ${active ? "animate-pulse" : ""}`} aria-label="Emergency SOS">
                <span className="flex flex-col items-center leading-none"><Siren size={20} /><span className="mt-0.5 text-[9px] font-extrabold">SOS</span></span>
            </button>
            {open && (
                <div className="fixed inset-0 z-[80] flex items-end justify-center bg-navy/60" onClick={() => setOpen(false)}>
                    <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()} data-testid="care-sos-sheet">
                        <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2"><Siren size={20} className="text-red-600" /><h3 className="font-heading text-lg font-extrabold text-navy">Emergency SOS</h3></div>
                            <button onClick={() => setOpen(false)} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button>
                        </div>
                        <a href="tel:112" data-testid="sos-call-112" className="mb-3 flex items-center justify-center gap-2 rounded-full border-2 border-red-600 py-3 text-sm font-bold text-red-600"><Phone size={16} /> Life-threatening? Call 112 now</a>
                        {active ? (
                            <div className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="sos-active-case">
                                <p className="font-heading text-sm font-extrabold text-red-700">{active.case_id} · {active.status.replace("_", " ")}</p>
                                <p className="mt-0.5 text-xs text-red-700/80">HOMIIE Care team has your details and is tracking this case.</p>
                                <div className="mt-3"><Timeline items={active.timeline} accent="#DC2626" testid="sos-timeline" title="Case updates" /></div>
                            </div>
                        ) : (
                            <>
                                <p className="mb-3 text-sm text-navy/60">This alerts the HOMIIE Care team and linked family instantly with the patient's details, health records and location.</p>
                                <label className="mb-1 block text-xs font-semibold text-navy/60">Who needs help?</label>
                                <select data-testid="sos-member" value={memberId} onChange={(e) => setMemberId(e.target.value)} className="mb-3 w-full rounded-xl border border-navy/15 px-3 py-3 text-sm outline-none focus:border-red-500">
                                    {members.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.relation})</option>)}
                                </select>
                                <textarea data-testid="sos-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="What happened? (optional) e.g. fell down, breathless, chest pain" className="mb-4 w-full rounded-xl border border-navy/15 px-3 py-2.5 text-sm outline-none focus:border-red-500" />
                                {count !== null ? (
                                    <div className="flex gap-2">
                                        <div data-testid="sos-countdown" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-red-600 py-4 text-base font-extrabold text-white"><Siren size={20} className="animate-pulse" /> Sending in {count}…</div>
                                        <button data-testid="sos-cancel-countdown" onClick={() => setCount(null)} className="rounded-full border-2 border-navy/20 px-5 text-sm font-bold text-navy">Cancel</button>
                                    </div>
                                ) : (
                                <button data-testid="sos-send-btn" disabled={busy} onClick={() => setCount(3)} className="flex w-full items-center justify-center gap-2 rounded-full bg-red-600 py-4 text-base font-extrabold text-white shadow-lg shadow-red-600/30 active:scale-95 disabled:opacity-60">
                                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Siren size={20} />} SEND SOS NOW
                                </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
